/** @file facet.addEntry: inserts a facet entry into an impostor (later entries' slot keys shift). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { deriveSlots } from '@zilo/pose3d-format/derive/deriveSlots';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import { checkShape } from '@zilo/pose3d-format/validate/fields/checkShape';
import { FACET_ENTRY_SHAPE } from '@zilo/pose3d-format/validate/shapes/facetEntryShape';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { findDrawable } from '../drawable/findDrawable';
import { pivotWarnings } from '../drawable/pivotWarnings';
import { requireDrawnAzimuths } from './requireDrawnAzimuths';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';

const entryCheck = checkShape(FACET_ENTRY_SHAPE);

/**
 * `facet.addEntry { drawable, entry, index? }` → `{ entry, slots }` (default: append; a `_FLIP` twin entry needs
 * `drawnAzimuths`); inverse: `facet.removeEntry`.
 */
export const facetAddEntryCommand: CommandDef<'facet.addEntry'> = {
  name: 'facet.addEntry',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['drawable', 'entry', 'index']);
    return compact({ drawable: r.str('drawable'), entry: r.check('entry', entryCheck) as FacetEntry, index: r.opt('index', (k) => r.int(k, { min: 0 })) });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const { index: di, drawable } = findDrawable(draft.puppet, input.drawable);
    if (drawable.kind !== 'impostor') throwError(ERROR_CODES.VALIDATION, { kind: drawable.kind }, { path: '/input/drawable', hint: 'facet entries belong to impostors' });
    requireDrawnAzimuths(draft.puppet, input.entry, '/input/entry');
    const at = input.index ?? drawable.facets.length;
    if (at > drawable.facets.length) throwError(ERROR_CODES.VALIDATION, { index: at }, { path: '/input/index', hint: `0..${drawable.facets.length}` });
    drawable.facets.splice(at, 0, structuredClone(input.entry));
    checkPuppet(draft, ctx);
    const slots = deriveSlots(draft.puppet).filter((s) => s.drawable === input.drawable && s.entry === at);
    return {
      result: { entry: at, slots }, warnings: pivotWarnings(drawable, di),
      inverse: [{ name: 'facet.removeEntry', input: { drawable: input.drawable, entry: at } }],
    };
  },
};
